'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
type Recipient = RouterOutputs['spaces']['memberlist']['users'][number];
type Props = {
  closeHandler: () => void;
  spaceSlug: string;
  fromUser: {
    id: number;
    username: string;
    name: string | null;
  };
  onSuccess?: () => void;
};
const TransferCardsModal = t0 => {
  const $ = _c(57);
  const {
    closeHandler,
    spaceSlug,
    fromUser,
    onSuccess
  } = t0;
  const queryClient = useQueryClient();
  const [recipient, setRecipient] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  let t1;
  let t2;
  if ($[0] !== spaceSlug) {
    t1 = ["SpaceMembersList", spaceSlug];
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return unwrapFromResult(client.spaces.memberlist.query({
        slug: spaceSlug,
        cursor: pageParam
      }));
    };
    $[0] = spaceSlug;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      initialPageParam: undefined as number | undefined,
      getNextPageParam: _temp,
      select: _temp3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data,
    isLoading
  } = useInfiniteQuery(t3);
  let T0;
  let T1;
  let submit;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[6] !== closeHandler || $[7] !== data || $[8] !== fromUser.id || $[9] !== fromUser.name || $[10] !== fromUser.username || $[11] !== isLoading || $[12] !== onSuccess || $[13] !== queryClient || $[14] !== recipient || $[15] !== spaceSlug) {
    let t8;
    if ($[23] !== fromUser.id) {
      t8 = u => u.id !== fromUser.id && !u.isInvited;
      $[23] = fromUser.id;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    const candidates = (data ?? []).filter(t8);
    let t9;
    if ($[25] !== closeHandler || $[26] !== fromUser.id || $[27] !== onSuccess || $[28] !== queryClient || $[29] !== recipient || $[30] !== spaceSlug) {
      t9 = async () => {
        if (!recipient) {
          return;
        }
        setSubmitting(true);
        const [err, result] = await client.spaces.transferUserCards.mutate({
          slug: spaceSlug,
          fromUserId: fromUser.id,
          toUserId: recipient.id
        });
        setSubmitting(false);
        if (err) {
          toast(err.message ?? "Failed to transfer cards.", "", "error");
          return;
        }
        toast(`Transferred ${result.transferred} card${result.transferred === 1 ? "" : "s"} to @${recipient.username}.`);
        queryClient.invalidateQueries({
          predicate: q => {
            const key = q.queryKey;
            return Array.isArray(key) && typeof key[key.length - 1] === "string" && (key[key.length - 1] as string).endsWith(`-${spaceSlug}`);
          }
        });
        onSuccess?.();
        closeHandler();
      };
      $[25] = closeHandler;
      $[26] = fromUser.id;
      $[27] = onSuccess;
      $[28] = queryClient;
      $[29] = recipient;
      $[30] = spaceSlug;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    submit = t9;
    const sourceLabel = fromUser.name ? `${fromUser.name} (@${fromUser.username})` : `@${fromUser.username}`;
    T1 = Modal;
    t6 = closeHandler;
    let t10;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <h2>Transfer cards</h2>;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    if ($[33] !== closeHandler) {
      t7 = <ModalHeader closeHandler={closeHandler}>{t10}</ModalHeader>;
      $[33] = closeHandler;
      $[34] = t7;
    } else {
      t7 = $[34];
    }
    T0 = ModalContent;
    if ($[35] !== sourceLabel) {
      t4 = <p className="mb-3 text-sm text-textSecondary">Re-tag every card currently tagged with{" "}<span className="font-emphasis text-textPrimary">{sourceLabel}</span>{" "}in this space to another member.</p>;
      $[35] = sourceLabel;
      $[36] = t4;
    } else {
      t4 = $[36];
    }
    t5 = isLoading ? <div className="flex justify-center py-6"><Loader2 size={24} className="animate-spin text-textSecondary/70" /></div> : candidates.length === 0 ? <p className="py-6 text-center text-sm text-textSecondary/70">No eligible recipients in this space.</p> : <div className="max-h-[40vh] space-y-1 overflow-y-auto rounded-md border border-ds-border-standard p-2">{candidates.map(user => {
        const selected = recipient?.id === user.id;
        return <button key={user.id} type="button" onClick={() => setRecipient(user)} className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors ${selected ? "bg-primary/15 ring-1 ring-primary" : "hover:bg-background-light"}`}><Avatar avatarKey={user.avatarKey} robohash={user.robohash ?? ""} isBot={user.tag === "BOT"} size={32} userId={user.id} /><div className="min-w-0 flex-1"><p className="truncate text-sm">{user.name || `@${user.username}`}</p>{user.name && <p className="truncate text-xs text-textSecondary/60">@{user.username}</p>}</div></button>;
      })}</div>;
    $[6] = closeHandler;
    $[7] = data;
    $[8] = fromUser.id;
    $[9] = fromUser.name;
    $[10] = fromUser.username;
    $[11] = isLoading;
    $[12] = onSuccess;
    $[13] = queryClient;
    $[14] = recipient;
    $[15] = spaceSlug;
    $[16] = T0;
    $[17] = T1;
    $[18] = submit;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    T0 = $[16];
    T1 = $[17];
    submit = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
  }
  let t8;
  if ($[37] !== T0 || $[38] !== t4 || $[39] !== t5) {
    t8 = <T0>{t4}{t5}</T0>;
    $[37] = T0;
    $[38] = t4;
    $[39] = t5;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== closeHandler) {
    t9 = <Button text="Cancel" variant="SECONDARY" onClick={closeHandler} />;
    $[41] = closeHandler;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  const t10 = recipient ? `Transfer to @${recipient.username}` : "Transfer";
  const t11 = !recipient || submitting;
  let t12;
  if ($[43] !== submit || $[44] !== submitting || $[45] !== t10 || $[46] !== t11) {
    t12 = <Button text={t10} onClick={submit} disabled={t11} loading={submitting} />;
    $[43] = submit;
    $[44] = submitting;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t12 || $[49] !== t9) {
    t13 = <ModalFooter>{t9}{t12}</ModalFooter>;
    $[48] = t12;
    $[49] = t9;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== T1 || $[52] !== t13 || $[53] !== t6 || $[54] !== t7 || $[55] !== t8) {
    t14 = <T1 closeHandler={t6}>{t7}{t8}{t13}</T1>;
    $[51] = T1;
    $[52] = t13;
    $[53] = t6;
    $[54] = t7;
    $[55] = t8;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  return t14;
};
export default TransferCardsModal;
function _temp(lastPage) {
  if (lastPage.users.length < 10) {
    return;
  }
  return lastPage.users[lastPage.users.length - 1]?.spaceMemberId;
}
function _temp2(p) {
  return p.users;
}
function _temp3(d) {
  return d.pages.flatMap(_temp2);
}